import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useT } from '../lib/i18n';
import { BACKEND_URL as API } from '../lib/config';

// ─────────────────────────────────────────────────────────────────────────
// ModernModelPortfolio — the «Moderno» theme "Model Portfolio": the companies
// of the latest iiReport (GET /api/public/model-portfolio, computed on read),
// every logo linking to that report as before. Header = Section Manager →
// Model Portfolio — Section Configuration. Colours from tokens + graphite.
// Approved by Anthony (s140): "Logo Ledger" (MP2) — the report as a graphite
// cover card beside a hairline grid of monochrome logos that turn to colour
// on hover/focus.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";
const GRAPHITE = '#16171a';
const shell = 'max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)] py-20 lg:py-28';

const src = (s) => (s && s.startsWith('/api') ? `${API}${s}` : s);

// "iiReport — STOCK RANKING & PRICE TARGETS - September 25, 2026" → name + date
function splitTitle(t) {
  const m = String(t || '').match(/^(.*?)[\s–—-]+([A-Z][a-z]+ \d{1,2}, \d{4})\s*$/);
  const name = (m ? m[1] : String(t || '')).replace(/^iiReport\s*[—–-]\s*/i, '').trim();
  const pretty = name.toLowerCase().replace(/(^|\s|&\s)([a-z])/g, (a, p, c) => p + c.toUpperCase());
  return { name: pretty || 'Stock Ranking', date: m ? m[2] : '' };
}

const CSS = `
.mmp-logo{transition:filter 220ms ease,opacity 220ms ease}
.mmp-tile .mmp-color{opacity:0}
.mmp-tile .mmp-mono{opacity:1}
@media (hover:hover) and (pointer:fine){
  .mmp-tile:hover .mmp-color{opacity:1}.mmp-tile:hover .mmp-mono{opacity:0}
}
.mmp-tile:focus-visible .mmp-color{opacity:1}.mmp-tile:focus-visible .mmp-mono{opacity:0}
.mmp-tile:focus-visible,.mmp-cover:focus-visible{outline:2px solid var(--mmp-ring,var(--color-heading,#1a2332));outline-offset:2px}
@media (prefers-reduced-motion:reduce){.mmp-logo{transition-duration:0ms}}
`;

function Header({ cmsConfig, report, count }) {
  const tt = useT();
  const title = tt(cmsConfig.title) || 'Model Portfolio';
  const words = title.trim().split(/\s+/);
  const last = words.length > 1 ? words.pop() : null;
  const accent = 'var(--accent-strong, #b86500)';
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 lg:mb-14">
      <div className="flex flex-col gap-4 max-w-2xl">
        <div className="inline-flex items-center gap-3 text-[11px] font-bold uppercase" style={{ letterSpacing: '.2em', color: accent }}>
          <span style={{ width: 32, height: 2, background: 'var(--color-accent, #ff9c1b)' }} />{tt(cmsConfig.eyebrow) || `iiReport · ${report.date || report.name}`}
        </div>
        <h2 className="font-bold" style={{ fontFamily: SERIF, fontSize: 'clamp(2rem, 3.4vw, 3rem)', lineHeight: 1.08, color: 'var(--color-heading, #1a2332)' }} data-testid="model-portfolio-title">
          {words.join(' ')}{last && <> <em style={{ fontStyle: 'italic', fontWeight: 500, color: accent }}>{last}</em></>}
        </h2>
        <p className="text-base md:text-lg leading-relaxed" style={{ color: 'var(--color-body-text, #475569)' }}>
          {tt(cmsConfig.subtitle) || `The ${count} companies in our latest ${report.name} report.`}
        </p>
      </div>
      <a href={report.url} target="_blank" rel="noopener noreferrer" className="mx-link inline-flex items-center gap-2 font-bold text-sm self-start md:self-end" style={{ color: accent }} data-testid="model-portfolio-report">
        Read the full report <ArrowRight className="w-4 h-4" />
      </a>
    </div>
  );
}

/* ── Logo Ledger ───────────────────────────────────────────────────
   Hairline grid of monochrome logos that turn to colour on hover/focus (on
   phones they stay monochrome); the report as a cover card beside it. */
function LedgerVariant({ list, url, report, count }) {
  return (
    <div className="grid gap-8 lg:gap-12 lg:grid-cols-[minmax(0,300px)_minmax(0,1fr)] items-start">
      <a href={url} target="_blank" rel="noopener noreferrer" className="mmp-cover group flex flex-col gap-4 p-6 lg:p-7 rounded-[3px] lg:sticky lg:top-28" style={{ background: GRAPHITE, '--mmp-ring': '#fff' }} data-testid="model-portfolio-cover">
        <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '.16em', color: 'var(--color-accent, #ff9c1b)' }}>iiReport</span>
        <span className="text-white font-semibold" style={{ fontFamily: SERIF, fontSize: '1.45rem', lineHeight: 1.2 }}>{report.name}</span>
        <span className="flex items-end justify-between gap-4 pt-4 border-t" style={{ borderColor: 'rgba(255,255,255,.14)' }}>
          <span className="flex flex-col">
            <span className="text-white font-bold tabular-nums" style={{ fontSize: '2.4rem', lineHeight: 1 }}>{count}</span>
            <span className="text-white/60 text-xs mt-1">companies</span>
          </span>
          {report.date && <span className="text-white/60 text-xs text-right">{report.date}</span>}
        </span>
      </a>
      <div className="grid grid-cols-2 sm:grid-cols-3 border-t border-l" style={{ borderColor: 'rgba(20,26,58,.1)' }}>
        {list.map(c => (
          <a key={c.symbol} href={url} target="_blank" rel="noopener noreferrer" aria-label={`${c.name} (${c.symbol})`} title={c.name} data-testid={`mp-logo-${c.symbol}`}
            className="mmp-tile relative flex flex-col items-center justify-center gap-3 border-r border-b px-4 py-7 min-h-[118px]" style={{ borderColor: 'rgba(20,26,58,.1)' }}>
            <span className="relative grid place-items-center h-10 w-full">
              <img src={src(c.logo_off || c.logo)} alt="" loading="lazy" className="mmp-logo mmp-mono col-start-1 row-start-1 max-h-10 max-w-[70%] w-auto object-contain" />
              <img src={src(c.logo)} alt="" loading="lazy" className="mmp-logo mmp-color col-start-1 row-start-1 max-h-10 max-w-[70%] w-auto object-contain" />
            </span>
            <span className="text-[11px] font-bold tabular-nums" style={{ letterSpacing: '.1em', color: 'rgba(20,26,58,.45)' }}>{c.symbol}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

export default function ModernModelPortfolio({ cmsConfig = {} }) {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    fetch(`${API}/api/public/model-portfolio`).then(r => r.json())
      .then(d => { if (alive) setData(d); })
      .catch(() => { if (alive) setData({ enabled: false, companies: [] }); });
    return () => { alive = false; };
  }, []);
  const list = (data && data.enabled && data.companies) || [];
  if (!list.length) return null;
  const report = { ...splitTitle(data.post_title), url: data.post_url || '#' };
  const count = data.count || list.length;
  return (
    <section id="model-portfolio" data-testid="model-portfolio-section" className="bg-white">
      <style>{CSS}</style>
      <div className={shell}>
        <Header cmsConfig={cmsConfig} report={report} count={count} />
        <LedgerVariant list={list} url={report.url} report={report} count={count} />
      </div>
    </section>
  );
}
